import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save, Loader2, Info } from 'lucide-react';

const AdminBranchNoticePage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [content, setContent] = useState('');

    useEffect(() => {
        fetchNotice();
    }, []);

    const fetchNotice = async () => {
        try {
            const response = await axios.get('/api/admin/branch-locator/notice');
            if (response.data.success && response.data.data) {
                setContent(response.data.data.content);
            }
        } catch (error) {
            console.error('Error fetching notice:', error);
            toast.error('Failed to load notice content');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const response = await axios.put('/api/admin/branch-locator/notice', {
                content
            });
            if (response.data.success) {
                toast.success('Notice updated successfully');
            }
        } catch (error) {
            console.error('Error updating notice:', error);
            toast.error('Failed to update notice');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Branch Locator Notice</h1>
                    <p className="text-slate-500">Manage the notice text displayed on the Branch Locator page.</p>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
                        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-700 space-y-1">
                            <p className="font-medium">HTML Formatting Supported</p>
                            <p>You can use basic HTML tags for styling, such as:</p>
                            <ul className="list-disc ml-4 mt-1 space-y-0.5">
                                <li><code>&lt;span class="font-semibold"&gt;Text&lt;/span&gt;</code> for bold text</li>
                                <li><code>&lt;br /&gt;</code> for line breaks</li>
                            </ul>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Notice Content
                        </label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            rows={6}
                            className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-primary-100 focus:border-primary-600 outline-none transition-all font-mono text-sm"
                            placeholder="Enter notice content here..."
                            required
                        />
                        <p className="mt-2 text-xs text-slate-400">
                            This text will appear in the blue bar at the bottom of the Branch Locator page.
                        </p>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-slate-100">
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-5 h-5" />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminBranchNoticePage;
